import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { postRequest, getRequest } from 'apis/ApiInstence';
import { toast } from 'react-toastify';
import { PRODUCT_OPTION_LABELS, PRODUCT_OPTION_PRESETS, WEIGHT_LABELS } from 'constants/productDropdownOptions';

const AddProduct = () => {
  const OTHER_VALUE = '__other__';
  const NUMERIC_FIELDS = new Set(['selling_price', 'shipping_charge', 'original_price', 'stock_quantity', 'weight', 'rating']);

  const [optionSelections, setOptionSelections] = useState({});
  const [customOptionValues, setCustomOptionValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [mainCategories, setMainCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [filteredSubCategories, setFilteredSubCategories] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    selling_price: '',
    shipping_charge: '',
    original_price: '',
    description: '',
    benefit_description: '',
    category_id: '',
    main_category_id: '',
    sub_category_id: '',

    stock_quantity: '',
    weight: '',
    status: 'active',
    rating: ''
  });

  const [firstImage, setFirstImage] = useState(null);
  const [moreImages, setMoreImages] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catRes, mainCatRes, subCatRes] = await Promise.all([
          getRequest('categories'),
          getRequest('main-categories'),
          getRequest('sub-categories'),
        ]);
        setCategories(catRes?.data || []);
        setMainCategories(mainCatRes?.data || []);
        setSubCategories(subCatRes?.data || []);
      } catch (err) {
        console.error('Failed to fetch category data:', err);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (formData.category_id) {
      setFilteredSubCategories(subCategories.filter((s) => Number(s.category_id) === Number(formData.category_id)));
    } else {
      setFilteredSubCategories([]);
    }
  }, [formData.category_id, subCategories]);

  const formatOptionLabel = (fieldName, value) => {
    if (fieldName === 'weight') return WEIGHT_LABELS[value] || `${value}kg`;
    return value;
  };

  const normalizeNumberValue = (fieldName, rawValue) => {
    const str = String(rawValue ?? '').trim();
    if (!str) return '';
    const cleaned = fieldName === 'weight' ? str.replace(/[^0-9.]/g, '') : str;
    const n = Number.parseFloat(cleaned);
    if (Number.isNaN(n)) return '';
    return String(n);
  };

  const setFieldValue = (fieldName, value) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleOptionSelectChange = (fieldName, selectedValue) => {
    setOptionSelections((prev) => ({ ...prev, [fieldName]: selectedValue }));

    if (!selectedValue) {
      setCustomOptionValues((prev) => ({ ...prev, [fieldName]: '' }));
      setFieldValue(fieldName, '');
      return;
    }

    if (selectedValue === OTHER_VALUE) {
      const existingCustom = customOptionValues[fieldName] || '';
      setFieldValue(fieldName, existingCustom);
      return;
    }

    setCustomOptionValues((prev) => ({ ...prev, [fieldName]: '' }));
    setFieldValue(fieldName, selectedValue);
  };

  const handleCustomOptionChange = (fieldName, value) => {
    setCustomOptionValues((prev) => ({ ...prev, [fieldName]: value }));
    setFieldValue(fieldName, value);
  };

  const renderSelectWithOther = (fieldName) => {
    const options = PRODUCT_OPTION_PRESETS[fieldName] || [];
    const label = PRODUCT_OPTION_LABELS[fieldName] || fieldName;
    const selectedValue = optionSelections[fieldName] ?? '';
    const customValue = customOptionValues[fieldName] ?? '';

    return (
      <>
        <select
          id={fieldName}
          name={fieldName}
          value={selectedValue}
          onChange={(e) => handleOptionSelectChange(fieldName, e.target.value)}
          className="form-control"
        >
          <option value="">{`Select ${label}`}</option>
          {options.map((value) => (
            <option key={value} value={value}>
              {formatOptionLabel(fieldName, value)}
            </option>
          ))}
          <option value={OTHER_VALUE}>Other (Type)</option>
        </select>

        {selectedValue === OTHER_VALUE && (
          <input
            type="text"
            className="form-control mt-2"
            placeholder={`Enter ${label}`}
            value={customValue}
            onChange={(e) => handleCustomOptionChange(fieldName, e.target.value)}
          />
        )}
      </>
    );
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;

    const formDataToSend = new FormData();
    for (const key in formData) {
      const valueToSend = NUMERIC_FIELDS.has(key) ? normalizeNumberValue(key, formData[key]) : formData[key];
      formDataToSend.append(key, valueToSend);
    }

    if (firstImage) {
      formDataToSend.append('images', firstImage);
    }
    for (let i = 0; i < moreImages.length; i++) {
      formDataToSend.append('images', moreImages[i]);
    }

    try {
      setSaving(true);
      await postRequest('products', formDataToSend);
      toast.success('Product added successfully!', {
        position: 'top-right',
        autoClose: 2000
      });
      setTimeout(() => {
        navigate('/products');
      }, 2000);
    } catch (err) {
      console.error('Failed to add product:', err);
      const msg =
        err?.message ||
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        'Something went wrong. Please try again.';
      toast.error(msg);
      setSaving(false);
    }
  };

  return (
    <div className="container-fluid mt-5">
      <div className="mainheadig mx-4">
        <h4 className="text-white font-weight-bold">Add Product</h4>
      </div>
      <form onSubmit={handleSubmit} encType="multipart/form-data">
        <div className="row mx-4 mt-5">
          <div className="col-sm-4 mt-2">
            <label htmlFor="product_name" className="admin-label">
              Product Name
            </label>
            <input id="product_name" type="text" name="name" placeholder="Enter Product Name" value={formData.name} onChange={handleChange} className="form-control" required />
          </div>

          <div className="col-sm-4 mt-2">
            <label htmlFor="selling_price" className="admin-label">
              Selling Price
            </label>
            <input
              id="selling_price"
              type="number"
              step="1"
              name="selling_price"
              placeholder='Enter selling price'
              value={formData.selling_price}
              onChange={handleChange}
              className="form-control"
              required
            />
          </div>

          <div className="col-sm-4 mt-2">
            <label htmlFor="shipping_charge" className="admin-label">
              Shipping Charge
            </label>
            <input
              id="shipping_charge"
              type="number"
              step="0.01"
              name="shipping_charge"
              placeholder='Enter shipping charge'
              value={formData.shipping_charge}
              onChange={handleChange}
              className="form-control"
            />
          </div>

          <div className="col-sm-4 mt-2">
            <label htmlFor="original_price" className="admin-label">
              Original Price
            </label>
            <input
              id="original_price"
              type="number"
              step="1"
              name="original_price"
              placeholder='Enter original price'
              value={formData.original_price}
              onChange={handleChange}
              className="form-control"
            />
          </div>

          <div className="col-sm-4 mt-2">
            <label htmlFor="stock_quantity" className="admin-label">
              Stock Quantity
            </label>
            <input
              id="stock_quantity"
              type="number"
              step="1"
              name="stock_quantity"
              placeholder='Enter stock quantity'
              value={formData.stock_quantity}
              onChange={handleChange}
              className="form-control"
            />
          </div>

          <div className="col-sm-4 mt-2">
            <label htmlFor="weight" className="admin-label">
              Weight (kg)
            </label>
            {renderSelectWithOther('weight')}
          </div>

          <div className="col-sm-4 mt-2">
            <label htmlFor="rating" className="admin-label">
              Rating
            </label>
            {renderSelectWithOther('rating')}
          </div>

          <div className="col-sm-4 mt-2">
            <label htmlFor="status" className="admin-label">
              Status
            </label>
            <select id="status" name="status" value={formData.status} onChange={handleChange} className="form-control">
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div className="col-sm-4 mt-2">
            <label htmlFor="category_id" className="admin-label">Category</label>
            <select id="category_id" name="category_id" value={formData.category_id} onChange={handleChange} className="form-control">
              <option value="">Select Category</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>

          <div className="col-sm-4 mt-2">
            <label htmlFor="main_category_id" className="admin-label">Main Category</label>
            <select id="main_category_id" name="main_category_id" value={formData.main_category_id} onChange={handleChange} className="form-control">
              <option value="">Select Main Category</option>
              {mainCategories.map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>

          <div className="col-sm-4 mt-2">
            <label htmlFor="sub_category_id" className="admin-label">Sub Category</label>
            <select id="sub_category_id" name="sub_category_id" value={formData.sub_category_id} onChange={handleChange} className="form-control">
              <option value="">Select Sub Category</option>
              {filteredSubCategories.map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>

          <div className="col-sm-12 mt-4">
            <label htmlFor="description" className="admin-label">
              Description (Markdown)
            </label>
            <textarea
              id="description"
              rows={6}
              name="description"
              value={formData.description}
              onChange={handleChange}
              className="form-control"
              placeholder="Enter Description"
            ></textarea>
          </div>

          <div className="col-sm-12 mt-4">
            <label htmlFor="benefit_description" className="admin-label">
              Benefit Description
            </label>
            <textarea
              id="benefit_description"
              rows={4}
              name="benefit_description"
              value={formData.benefit_description}
              onChange={handleChange}
              className="form-control"
              placeholder="Enter Benefit Description"
            ></textarea>
          </div>

          <div className="col-sm-6 mt-4">
            <label htmlFor="firstImage" className="admin-label">
              Choose First Image
            </label>
            <input id="firstImage" type="file" className="form-control" accept="image/*" onChange={(e) => setFirstImage(e.target.files[0] || null)} />
            {firstImage && (
              <div className="mt-2">
                <img src={URL.createObjectURL(firstImage)} alt="First" width={100} height={100} className="border" style={{ objectFit: 'cover', borderRadius: 4 }} />
                <span className="ml-2 text-success font-weight-bold">First Image</span>
              </div>
            )}
          </div>

          <div className="col-sm-6 mt-4">
            <label htmlFor="moreImages" className="admin-label">
              Choose More Images
            </label>
            <input id="moreImages" type="file" className="form-control" accept="image/*" multiple onChange={(e) => setMoreImages(Array.from(e.target.files || []))} />
            {moreImages.length > 0 && (
              <div className="mt-2 d-flex flex-wrap">
                {moreImages.map((file, index) => (
                  <img key={index} src={URL.createObjectURL(file)} alt={`Preview ${index}`} width={80} height={80} className="mr-2 mb-2 border" style={{ objectFit: 'cover', borderRadius: 4 }} />
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-5 text-right mx-4">
          <button type="submit" className="btn btn-md addbtn" disabled={saving}>
            {saving ? 'Adding...' : 'Add Product'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddProduct;
